// Playback singleton around the Web Speech API. Exactly one block speaks at a
// time; starting a new one cancels the old. Components subscribe to learn
// whether they are the block currently speaking.

type Lyssnare = (spelarId: string | null) => void;

let spelarId: string | null = null;
const lyssnare = new Set<Lyssnare>();
let roster: SpeechSynthesisVoice[] = [];

// Monotonic counter, bumped once per tala() call. Utterance completion/error
// handlers close over the generation they were created in and only act if it
// is still current — this is what tells apart "the playback I belong to was
// replaced" from "the playback I belong to actually finished". Comparing
// spelarId/id instead (as a first version of this file did) is not enough:
// replaying the same id cancels an in-flight utterance whose error handler
// then fires later, and `spelarId === id` is still true at that point even
// though a newer call already owns the playback.
let generation = 0;

export function stodsAvWebblasaren(): boolean {
  return typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined";
}

function meddela(): void {
  for (const l of lyssnare) l(spelarId);
}

export function prenumerera(cb: Lyssnare): () => void {
  lyssnare.add(cb);
  cb(spelarId);
  return () => { lyssnare.delete(cb); };
}

function laddaRoster(): void {
  if (!stodsAvWebblasaren()) return;
  roster = window.speechSynthesis
    .getVoices()
    .filter((r) => r.lang.toLowerCase().startsWith("sv"));
}

export function svenskaRoster(): SpeechSynthesisVoice[] {
  if (roster.length === 0) laddaRoster();
  return roster;
}

// getVoices() is empty on the first call in Chrome — the list arrives later on
// the voiceschanged event. Anything rendering a voice picker must watch it.
export function bevakaRoster(cb: () => void): () => void {
  if (!stodsAvWebblasaren()) return () => {};
  laddaRoster();
  const hanterare = () => { laddaRoster(); cb(); };
  window.speechSynthesis.addEventListener("voiceschanged", hanterare);
  return () => window.speechSynthesis.removeEventListener("voiceschanged", hanterare);
}

// Swedish abbreviations whose internal/trailing period is not a sentence
// boundary. Checked with a plain endsWith lookup — no NLP, no dependency.
const FORKORTNINGAR = [
  "t.ex.",
  "bl.a.",
  "dvs.",
  "d.v.s.",
  "m.m.",
  "osv.",
  "s.k.",
  "f.d.",
  "ca.",
  "resp.",
  "jfr.",
];

function slutarMedForkortning(bit: string): boolean {
  const lower = bit.toLowerCase();
  return FORKORTNINGAR.some((f) => lower.endsWith(f));
}

// Chrome stops speaking mid-utterance after roughly 15 seconds. Splitting on
// sentence boundaries keeps every utterance short enough to dodge that, and
// gives the pauses a human reader would make. The lookbehind deliberately
// requires whitespace after the punctuation so "3,5 meter" stays one sentence.
//
// A first split pass over-splits at abbreviations like "t.ex." (their internal
// period is followed by whitespace, same as a real sentence end), so a second
// pass re-joins a piece with the next one whenever it ends in a known
// abbreviation.
export function delaIMeningar(text: string): string[] {
  const rabitar = text
    .split(/(?<=[.!?:])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const meningar: string[] = [];
  for (const bit of rabitar) {
    const foregaende = meningar[meningar.length - 1];
    if (foregaende !== undefined && slutarMedForkortning(foregaende)) {
      meningar[meningar.length - 1] = `${foregaende} ${bit}`;
    } else {
      meningar.push(bit);
    }
  }
  return meningar;
}

function valjRost(rostURI: string | null): SpeechSynthesisVoice | null {
  const alla = svenskaRoster();
  if (rostURI) {
    const traff = alla.find((r) => r.voiceURI === rostURI);
    if (traff) return traff;
  }
  return alla[0] ?? null;
}

export function tala(
  id: string,
  text: string,
  opt: { hastighet: number; rostURI: string | null },
): void {
  if (!stodsAvWebblasaren()) return;
  stoppa();
  const bitar = delaIMeningar(text);
  if (bitar.length === 0) return;

  const rost = valjRost(opt.rostURI);
  const minGeneration = ++generation;
  spelarId = id;
  meddela();

  bitar.forEach((bit, i) => {
    const u = new SpeechSynthesisUtterance(bit);
    u.lang = "sv-SE";
    u.rate = opt.hastighet / 100;
    if (rost) u.voice = rost;
    const slutfor = () => {
      if (generation === minGeneration) { spelarId = null; meddela(); }
    };
    if (i === bitar.length - 1) u.onend = slutfor;
    u.onerror = slutfor;
    window.speechSynthesis.speak(u);
  });
}

export function stoppa(): void {
  if (!stodsAvWebblasaren()) return;
  window.speechSynthesis.cancel();
  if (spelarId !== null) { spelarId = null; meddela(); }
}
